// Package imports:
import React from 'react';
// Service imports:
import { formatDate, formatNumber } from '../../services/utils';
// Type imports:
import { PublicOrderTickData } from '../../types/api/PublicTypes';
import { WithBook } from '../../types/api/Types';
import { OpenClosingPrice } from '../../types/api/OpenClosingPrice';

interface IOwnProps {
    orderId: string;
    symbol: string;
    date: string;
    orderEvents: WithBook<PublicOrderTickData>[];
    firstOrderEvent: WithBook<PublicOrderTickData> | null;
    lastOrderEvent: WithBook<PublicOrderTickData> | null;
    hasPrivateData: boolean;
    spreadPosition: { label: string; detail: string } | null;
    timeStats: {
        durationMs: number | null;
        durationFormatted: string;
        eventCount: number;
        updateCount: number;
        numFills: number;
        orderToTradeRatio: string;
    } | null;
    ohlcData: OpenClosingPrice | null;
    bondFields: {
        yield: number | null;
        dirtyPrice: number | null;
        duration: number | null;
        pricePerPoint: number | null;
    } | null;
    securityType: 'share' | 'bond' | 'derivative' | 'etf' | null;
}

const OrderSummary: React.FC<IOwnProps> = ({
    orderId,
    symbol,
    date,
    orderEvents,
    firstOrderEvent,
    lastOrderEvent,
    hasPrivateData,
    spreadPosition,
    timeStats,
    ohlcData,
    bondFields,
    securityType
}) => {
    const side = firstOrderEvent?.tick.side ?? '—';
    const member = firstOrderEvent?.tick.member ?? '—';
    const orderClass = firstOrderEvent?.tick.order_class ?? '—';
    const sideClass = side === 'bid' ? 'SideBuy' : side === 'ask' ? 'SideSell' : '';

    // Get lifecycle: sequence of update codes with timestamps
    const lifecycle = orderEvents.map(({ tick }) => ({
        code: tick.update_code,
        timestamp: tick.timestamp,
        price: tick.price,
        quantity: tick.quantity
    }));

    return (
        <div id='OrderSummary'>
            <div className='SummaryHeader'>
                <h4>Order Execution Analysis</h4>
            </div>
            <div className='SummaryGrid'>
                <div className='SummaryItem'>
                    <span className='Label'>Order ID</span>
                    <span className='Value'>{orderId}</span>
                </div>
                <div className='SummaryItem'>
                    <span className='Label'>Symbol</span>
                    <span className='Value'>{symbol}</span>
                </div>
                <div className='SummaryItem'>
                    <span className='Label'>Date</span>
                    <span className='Value'>{date}</span>
                </div>
                <div className='SummaryItem'>
                    <span className='Label'>Side</span>
                    <span className={`Value ${sideClass}`}>
                        {side === 'bid' ? 'Buy' : side === 'ask' ? 'Sell' : side}
                    </span>
                </div>
                <div className='SummaryItem'>
                    <span className='Label'>Member</span>
                    <span className='Value'>{member}</span>
                </div>
                <div className='SummaryItem'>
                    <span className='Label'>Order Class</span>
                    <span className='Value'>{orderClass}</span>
                </div>
                <div className='SummaryItem'>
                    <span className='Label'>Events</span>
                    <span className='Value'>{orderEvents.length}</span>
                </div>
                {lastOrderEvent && (
                    <>
                        <div className='SummaryItem'>
                            <span className='Label'>Last Price</span>
                            <span className='Value'>{formatNumber(lastOrderEvent.tick.price)}</span>
                        </div>
                        <div className='SummaryItem'>
                            <span className='Label'>Last Quantity</span>
                            <span className='Value'>{formatNumber(lastOrderEvent.tick.quantity, 'volume')}</span>
                        </div>
                        <div className='SummaryItem'>
                            <span className='Label'>Last Value</span>
                            <span className='Value'>{formatNumber(lastOrderEvent.tick.value, 'volume')}</span>
                        </div>
                    </>
                )}
            </div>

            {/* Spread Position + Time Stats + Price Context */}
            <div className='AnalyticsSummary'>
                {spreadPosition && (
                    <div className='AnalyticsItem'>
                        <span className='Label'>Spread Position</span>
                        <span className={`Value SpreadBadge ${spreadPosition.label.replace(/\s/g, '')}`}>
                            {spreadPosition.label}
                        </span>
                        <span className='Detail'>{spreadPosition.detail}</span>
                    </div>
                )}
                {timeStats && (
                    <>
                        <div className='AnalyticsItem'>
                            <span className='Label'>Duration</span>
                            <span className='Value'>{timeStats.durationFormatted}</span>
                        </div>
                        <div className='AnalyticsItem'>
                            <span className='Label'>Updates</span>
                            <span className='Value'>{timeStats.updateCount}</span>
                        </div>
                        <div className='AnalyticsItem'>
                            <span className='Label'>Fills</span>
                            <span className='Value'>{timeStats.numFills}</span>
                        </div>
                        <div className='AnalyticsItem'>
                            <span className='Label'>Update/Trade Ratio</span>
                            <span className='Value'>{timeStats.orderToTradeRatio}</span>
                        </div>
                    </>
                )}
                {ohlcData && (
                    <>
                        <div className='AnalyticsItem'>
                            <span className='Label'>Day Open</span>
                            <span className='Value'>{formatNumber(ohlcData.open)}</span>
                        </div>
                        <div className='AnalyticsItem'>
                            <span className='Label'>Day High</span>
                            <span className='Value'>{formatNumber(ohlcData.high)}</span>
                        </div>
                        <div className='AnalyticsItem'>
                            <span className='Label'>Day Low</span>
                            <span className='Value'>{formatNumber(ohlcData.low)}</span>
                        </div>
                        <div className='AnalyticsItem'>
                            <span className='Label'>Day Close</span>
                            <span className='Value'>{formatNumber(ohlcData.close)}</span>
                        </div>
                        <div className='AnalyticsItem'>
                            <span className='Label'>Day Volume</span>
                            <span className='Value'>{formatNumber(ohlcData.volume, 'volume')}</span>
                        </div>
                        <div className='AnalyticsItem'>
                            <span className='Label'>Day Trades</span>
                            <span className='Value'>{ohlcData.number_of_trades}</span>
                        </div>
                    </>
                )}
            </div>

            {/* Bond-specific fields */}
            {bondFields && (
                <div className='BondFields'>
                    <h6>Bond Data</h6>
                    <div className='SummaryGrid'>
                        {bondFields.yield !== null && (
                            <div className='SummaryItem'>
                                <span className='Label'>Yield</span>
                                <span className='Value'>{formatNumber(bondFields.yield)}</span>
                            </div>
                        )}
                        {bondFields.dirtyPrice !== null && (
                            <div className='SummaryItem'>
                                <span className='Label'>Dirty Price</span>
                                <span className='Value'>{formatNumber(bondFields.dirtyPrice)}</span>
                            </div>
                        )}
                        {bondFields.duration !== null && (
                            <div className='SummaryItem'>
                                <span className='Label'>Duration</span>
                                <span className='Value'>{formatNumber(bondFields.duration)}</span>
                            </div>
                        )}
                        {bondFields.pricePerPoint !== null && (
                            <div className='SummaryItem'>
                                <span className='Label'>Price Per Point</span>
                                <span className='Value'>{formatNumber(bondFields.pricePerPoint)}</span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Lifecycle Timeline */}
            {lifecycle.length > 0 && (
                <div className='LifecycleTimeline'>
                    <span className='Label'>Lifecycle</span>
                    <div className='TimelineSteps'>
                        {lifecycle.map((step, i) => (
                            <div key={i} className={`TimelineStep ${step.code ?? ''}`}>
                                <span className='StepCode'>{step.code}</span>
                                <span className='StepTime'>{formatDate(step.timestamp, true, true)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrderSummary;
